import { Button, cn } from "@tellescope/design-system";
import { usePermissionQuery } from "@tellescope/tanstack-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, ConfirmDialog } from "@tellescope/ui";
import { ChevronDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useStateWithFallback } from "@tellescope/react-hooks";
import { CustomizeHelperTemplateItem } from "./CustomizeHelperTemplateItem";
import { SelectContactDetailsBlockDialog } from "../../dialogs";
import { useState } from "react";
import { useCreateBlocks } from "../../../hooks/use-create-blocks";
import { useResolvedView } from "../../../hooks/use-resolved-view";
import { BlockProps, ContactDetailsBlockType } from "../../../types";
import { CUSTOMIZE_HELPER_TEMPLATES } from "./constants";

// TODO: abstract out expandable card in DS

export interface CustomizeHelperBlockProps {
  viewId: string;
  isFeatureEnabled?: BlockProps["isFeatureEnabled"];
  onViewMaterialized?: BlockProps["onViewMaterialized"];
}

export const CustomizeHelperBlock = ({ viewId, isFeatureEnabled, onViewMaterialized }: CustomizeHelperBlockProps) => {
  const { t } = useTranslation("contacts");

  const [isAddingBlocks, setIsAddingBlocks] = useState(false);
  const [pendingTemplateKey, setPendingTemplateKey] = useState<string>();

  const resolvedView = useResolvedView(viewId);

  const isUnresolved = resolvedView.mode === "loading" || resolvedView.mode === "error";
  const hasBlocks = isUnresolved ? undefined : resolvedView.blocks.length > 0;
  const [isExpanded, setIsExpanded] = useStateWithFallback<boolean>({
    fallbackValue: hasBlocks === false,
  });

  const {
    update: { access: canEdit },
  } = usePermissionQuery({
    model: "custom_dashboards",
    actions: ["update"],
    record: resolvedView.mode === "loading" || resolvedView.mode === "error" ? undefined : resolvedView.view,
  });

  const createBlocks = useCreateBlocks({
    viewId,
    onViewMaterialized,
  });

  const [isAddingInFlight, setIsAddingInFlight] = useState(false);

  /**
   * `createBlocks` is called from a click handler that cannot await it, so its rejection has to be
   * handled here rather than propagating as an unhandled one. Collapsing the card only after the write
   * resolves keeps a failed add visible: dismissing it first would read as success while nothing was
   * saved.
   *
   * The in-flight flag disables the template buttons for the duration of the write. A second add
   * started mid-flight is what breaks the first-time flow: materializing the placeholder view moves the
   * `viewId` prop onto the new record, so the second call's chain-guard no longer recognizes the first
   * call's pending write and neither has reached the cache yet.
   */
  const applyTemplate = (blockTypes: readonly ContactDetailsBlockType[]) => {
    setIsAddingInFlight(true);
    createBlocks([...blockTypes], { replace: true })
      .then(
        () => setIsExpanded(false),
        () => toast.error(t("The sections could not be added. Please try again."))
      )
      .finally(() => setIsAddingInFlight(false));
  };

  const handleUseTemplate = (template: (typeof CUSTOMIZE_HELPER_TEMPLATES)[number]) => {
    if (hasBlocks === false) {
      applyTemplate(template.blockTypes);
      return;
    }
    setPendingTemplateKey(template.key);
  };

  const pendingTemplate = CUSTOMIZE_HELPER_TEMPLATES.find((template) => template.key === pendingTemplateKey);

  return (
    <>
      <Card className="bg-ai-gradient-surface shadow-none gap-0 py-4">
        <CardHeader>
          <span className="flex items-center w-full justify-between">
            <CardTitle className="text-sm">{t("Customize")}</CardTitle>
            <span className="flex items-center">
              {canEdit && (
                <Button variant="ghost" iconOnly size="sm" color="muted" onClick={() => setIsAddingBlocks(true)}>
                  <Pencil className="size-4" />
                </Button>
              )}
              <Button variant="ghost" iconOnly size="sm" color="muted" onClick={() => setIsExpanded((prev) => !prev)}>
                <ChevronDown className={cn("size-4 transition-all", !isExpanded && "rotate-180")} />
              </Button>
            </span>
          </span>
          <CardDescription
            className={cn(
              "transition-[grid-template-rows] overflow-hidden grid grid-cols-1",
              isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            )}
          >
            <span className="min-h-0">
              {t("Here are some recommended configurations, you can add additional sections above.")}
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent
          className={cn(
            "transition-[grid-template-rows,padding] overflow-hidden max-h-min grid grid-cols-1",
            isExpanded ? "grid-rows-[1fr] pt-2" : "grid-rows-[0fr] pt-0"
          )}
        >
          <div className="min-h-0 space-y-2">
            {CUSTOMIZE_HELPER_TEMPLATES.map((template) => (
              <CustomizeHelperTemplateItem
                key={template.key}
                title={template.getTitle()}
                description={template.getDescription()}
                disabled={isAddingInFlight}
                onUseClick={() => handleUseTemplate(template)}
              />
            ))}
          </div>
        </CardContent>
      </Card>
      <SelectContactDetailsBlockDialog
        open={isAddingBlocks}
        setOpen={setIsAddingBlocks}
        viewId={viewId}
        isFeatureEnabled={isFeatureEnabled}
        onViewMaterialized={onViewMaterialized}
        onBlocksAdded={() => setIsAddingBlocks(false)}
      />
      <ConfirmDialog
        open={pendingTemplate !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingTemplateKey(undefined);
        }}
        variant="warning"
        title={t("Replace your current sections?")}
        description={t("Applying this configuration removes the sections you have now. This cannot be undone.")}
        confirmText={t("Replace sections")}
        cancelText={t("Cancel")}
        loading={isAddingInFlight}
        onConfirm={() => {
          if (!pendingTemplate) return;
          applyTemplate(pendingTemplate.blockTypes);
          setPendingTemplateKey(undefined);
        }}
      />
    </>
  );
};
